import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const fmtDate = d => d ? format(new Date(d), 'dd/MM/yyyy') : '—';
const fmtInr = n => `₹${parseFloat(n||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2})}`;

export default function Vouchers() {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get('/vouchers').then(r => { setVouchers(r.data); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const regenerate = async (v) => {
    setBusyId(v.id);
    try {
      const r = await api.post(`/vouchers/regenerate/shipment/${v.shipment_id}`);
      toast.success(`${v.voucher_no} regenerated`);
      if (viewing && viewing.id === v.id) setViewing(r.data.voucher || null);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setBusyId(null); }
  };

  const print = (v) => {
    const e = v.entries || {};
    const h = e.header || {};
    const lines = e.lines || [];
    const t = e.totals || {};
    const groups = {}; lines.forEach(l => { const k = l.section || 'Other'; if (!groups[k]) groups[k]=[]; groups[k].push(l); });
    const order = ['A. Basic Import','B. Shipping Line & Other','C. Custom Duty','D. CHA / Agency','E. Transportation','F. Bank & Misc','Other'];
    const keys = order.filter(k => groups[k]);
    const tfmt = n => parseFloat(n||0).toFixed(2);
    const lineRows = keys.map(k => `<tr class="sec"><td colspan="7">${k}</td></tr>` + groups[k].map(l => `<tr><td>${l.sno}</td><td>${l.desc}</td><td>${l.credit_to||''}</td><td>${l.debit_to||''}</td><td class="amt">${tfmt(l.amount)}</td><td class="amt">${l.gst?tfmt(l.gst):'—'}</td><td class="amt">${tfmt(l.total!=null?l.total:l.amount)}</td></tr>`).join('')).join('');
    const summaryRows = [
      ['Basic Import Value (INR)', t.basic_import_inr],
      ["Buyer's Credit", t.buyers_credit],
      ['Shipping — Net Cost', t.shipping_net],
      ['Custom — Net', t.custom_net],
      ['CHA — Net', t.cha_net],
      ['Transport — Net', t.transport_net],
      ['Bank Charges', t.bank_charges],
      ['Misc Charges', t.misc_charges],
    ].filter(([,v]) => v!=null && parseFloat(v)!==0).map(([l,v]) => `<tr><td>${l}</td><td class="amt">${tfmt(v)}</td></tr>`).join('');
    const w = window.open('','_blank');
    w.document.write(`<html><head><title>Journal Voucher ${v.voucher_no}</title><style>body{font-family:Arial,sans-serif;padding:24px;max-width:960px;margin:0 auto;color:#222}h2{color:#1e3a5f;margin-bottom:4px}.meta{display:grid;grid-template-columns:1fr 1fr 1fr;gap:4px 20px;margin:10px 0;font-size:12px}.meta b{color:#555}table{width:100%;border-collapse:collapse;margin-top:10px}th,td{padding:6px 8px;border:1px solid #cbd5e1;font-size:12px}th{background:#f1f5f9;text-align:left}.amt{text-align:right;font-variant-numeric:tabular-nums}.total{font-weight:700;background:#1e3a5f;color:#fff}.sec td{background:#eef6ff;font-weight:700;color:#1e3a5f}.sum{margin-top:12px;width:60%;margin-left:auto}.sig{display:flex;justify-content:space-between;margin-top:40px;font-size:12px}</style></head><body>
      <h2>Journal Voucher</h2>
      <div style="font-size:12px;color:#555">${v.voucher_no} · ${fmtDate(v.voucher_date)}</div>
      <div class="meta">
        <div><b>Order No:</b> ${h.order_no||v.order_no||''}</div><div><b>BL No:</b> ${h.bl_no||v.bl_no||''}</div><div><b>Party:</b> ${h.party||''}</div>
        <div><b>Origin:</b> ${h.origin||''}</div><div><b>Shipping Line:</b> ${h.shipping_line||''}</div><div><b>CHA:</b> ${h.cha||''}</div>
        <div><b>Weight:</b> ${h.wt_mt?parseFloat(h.wt_mt).toFixed(3):'—'} MT</div><div><b>Rate:</b> USD ${h.rate_usd||''}/MT</div><div><b>USD Value:</b> ${h.usd_value?parseFloat(h.usd_value).toFixed(2):'—'}</div>
        <div><b>Exchange Rate:</b> ${h.exchange_rate||''}</div><div><b>Containers:</b> ${h.containers||''}</div><div><b>Interest USD:</b> ${h.interest_usd||0}</div>
      </div>
      <div style="margin:8px 0;font-size:12px"><b>Narration:</b> ${v.narration||''}</div>
      <table><thead><tr><th>#</th><th>Description</th><th>Credit A/c</th><th>Debit A/c</th><th class="amt">Amount</th><th class="amt">GST</th><th class="amt">Total</th></tr></thead><tbody>${lineRows}</tbody>
      <tfoot><tr class="total"><td colspan="6">Total Landed Cost</td><td class="amt">${tfmt(v.total_landed_cost)}</td></tr></tfoot></table>
      <table class="sum"><thead><tr><th colspan="2">Summary</th></tr></thead><tbody>${summaryRows}<tr class="total"><td>Total Landed Cost</td><td class="amt">${tfmt(v.total_landed_cost)}</td></tr>${t.per_ton_cost ? `<tr><td>Per Ton Cost</td><td class="amt">${tfmt(t.per_ton_cost)}</td></tr>`:''}</tbody></table>
      <div class="sig"><div>Prepared By: _________________</div><div>Authorized By: _________________</div></div>
    </body></html>`);
    setTimeout(()=>w.print(), 200);
  };

  return (
    <div>
      <div className="page-header">
        <div><h2>Journal Vouchers</h2><p>Step 12 — Auto-generated on material receipt</p></div>
      </div>
      <div className="alert alert-info">These vouchers are for manual entry into your accounting software. They are auto-calculated from order and shipment data.</div>
      <div className="card" style={{padding:0}}>
        {loading ? <div className="loading">Loading...</div> : vouchers.length === 0 ? <div className="empty-state">No vouchers yet. They auto-generate on material receipt, or generate manually from Order → BL → Voucher tab.</div> :
          <div className="table-wrap">
            <table>
              <thead><tr><th>Voucher No</th><th>Date</th><th>Order No</th><th>BL No</th><th style={{textAlign:'right'}}>Basic Import</th><th style={{textAlign:'right'}}>Custom Duty</th><th style={{textAlign:'right'}}>Total Landed</th><th>Actions</th></tr></thead>
              <tbody>
                {vouchers.map(v => (
                  <tr key={v.id}>
                    <td className="fw-bold">{v.voucher_no}</td>
                    <td>{fmtDate(v.voucher_date)}</td>
                    <td className="text-primary" style={{cursor:'pointer'}} onClick={()=>navigate(`/orders/${v.order_id}`)}>{v.order_no}</td>
                    <td>{v.bl_no}</td>
                    <td style={{textAlign:'right'}}>{fmtInr(v.basic_import_value_inr)}</td>
                    <td style={{textAlign:'right'}}>{fmtInr(v.customs_duty)}</td>
                    <td className="fw-bold" style={{textAlign:'right'}}>{fmtInr(v.total_landed_cost)}</td>
                    <td>
                      <div className="btn-group">
                        <button className="btn btn-outline btn-xs" onClick={()=>setViewing(v)}>View</button>
                        <button className="btn btn-secondary btn-xs" onClick={()=>regenerate(v)} disabled={busyId===v.id}>{busyId===v.id?'...':'Regen'}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </div>

      {viewing && <JVDetailModal voucher={viewing} onClose={()=>setViewing(null)} onPrint={()=>print(viewing)} onRegenerate={()=>regenerate(viewing)} busy={busyId===viewing.id} onOpenOrder={()=>navigate(`/orders/${viewing.order_id}`)} />}
    </div>
  );
}

function SectionedJVTable({ lines, voucher }) {
  const groups = {};
  lines.forEach(l => { const k = l.section || 'Other'; if (!groups[k]) groups[k]=[]; groups[k].push(l); });
  const order = ['A. Basic Import','B. Shipping Line & Other','C. Custom Duty','D. CHA / Agency','E. Transportation','F. Bank & Misc','Other'];
  const keys = order.filter(k => groups[k]);
  return (
    <div className="table-wrap">
      <table>
        <thead><tr><th style={{width:40}}>#</th><th>Description</th><th>Credit A/c</th><th>Debit A/c</th><th style={{textAlign:'right'}}>Amount</th><th style={{textAlign:'right'}}>GST</th><th style={{textAlign:'right'}}>Total</th></tr></thead>
        <tbody>
          {keys.map(k => (
            <React.Fragment key={k}>
              <tr style={{background:'#eef6ff'}}><td colSpan={7} style={{fontWeight:700,color:'#1e3a5f'}}>{k}</td></tr>
              {groups[k].map(l => (
                <tr key={l.sno}>
                  <td>{l.sno}</td>
                  <td>{l.desc}</td>
                  <td className="text-muted" style={{fontSize:12}}>{l.credit_to||'—'}</td>
                  <td className="text-muted" style={{fontSize:12}}>{l.debit_to||'—'}</td>
                  <td style={{textAlign:'right'}}>{fmtInr(l.amount)}</td>
                  <td style={{textAlign:'right'}}>{l.gst?fmtInr(l.gst):'—'}</td>
                  <td style={{textAlign:'right',fontWeight:600}}>{fmtInr(l.total!=null?l.total:l.amount)}</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
          {lines.length===0 && <tr><td colSpan={7} className="empty-state">No line items.</td></tr>}
          <tr style={{background:'#f1f5f9',fontWeight:700}}>
            <td colSpan={6}>Total Landed Cost</td>
            <td style={{textAlign:'right'}}>{fmtInr(voucher.total_landed_cost)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function JVSummaryGrid({ totals, voucher }) {
  const rows = [
    ['Basic Import Value (INR)', totals.basic_import_inr],
    ['Buyer\'s Credit', totals.buyers_credit],
    ['Shipping — Gross', totals.shipping_gross],
    ['Shipping — GST Credit (-)', totals.shipping_gst_credit],
    ['Shipping — Net Cost', totals.shipping_net],
    ['Custom Duty', totals.custom_duty],
    ['Custom IGST Credit (-)', totals.custom_igst_credit],
    ['Custom Interest', totals.custom_interest],
    ['Custom — Net', totals.custom_net],
    ['CHA Agency Basic', totals.cha_basic],
    ['CHA GST / TDS', (totals.cha_gst||0) + (totals.cha_tds||0)],
    ['CHA — Net', totals.cha_net],
    ['Transport Basic', totals.transport_basic],
    ['Transport TDS (-)', totals.transport_tds],
    ['Transport — Net', totals.transport_net],
    ['Bank Charges', totals.bank_charges],
    ['Misc Charges', totals.misc_charges],
  ].filter(([,v]) => v!=null && parseFloat(v)!==0);
  return (
    <div className="card-sm" style={{background:'#f8fafc',marginTop:10}}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(2, 1fr)',gap:'4px 20px',fontSize:13}}>
        {rows.map(([l,v]) => (
          <div key={l} style={{display:'flex',justifyContent:'space-between',borderBottom:'1px dashed #e2e8f0',padding:'3px 0'}}>
            <span>{l}</span><span style={{fontVariantNumeric:'tabular-nums'}}>{fmtInr(v)}</span>
          </div>
        ))}
        <div style={{gridColumn:'span 2',display:'flex',justifyContent:'space-between',padding:'8px 0 0',borderTop:'2px solid #1e3a5f',marginTop:4,fontWeight:700,fontSize:14,color:'#1e3a5f'}}>
          <span>Total Landed Cost</span><span>{fmtInr(voucher.total_landed_cost)}</span>
        </div>
        {totals.per_ton_cost ? <div style={{gridColumn:'span 2',display:'flex',justifyContent:'space-between',fontWeight:600,color:'#475569'}}>
          <span>Per Ton Cost</span><span>{fmtInr(totals.per_ton_cost)}</span>
        </div> : null}
      </div>
    </div>
  );
}

function JVDetailModal({ voucher, onClose, onPrint, onRegenerate, busy, onOpenOrder }) {
  const h = voucher.entries?.header || {};
  const lines = voucher.entries?.lines || [];
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" style={{maxWidth:'960px'}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <h3>Journal Voucher — {voucher.voucher_no}</h3>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
        </div>
        <div>
          <div className="alert alert-info" style={{marginTop:0}}>Auto-calculated landed-cost breakup. Update shipment fields and click <b>Regenerate</b> to refresh.</div>

          <div className="card-sm" style={{background:'#f8fafc',marginBottom:12}}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4, 1fr)',gap:'8px 16px',fontSize:13}}>
              <div><b>Date:</b> {fmtDate(voucher.voucher_date)}</div>
              <div><b>Order No:</b> {h.order_no||voucher.order_no||'—'}</div>
              <div><b>BL No:</b> {h.bl_no||voucher.bl_no||'—'}</div>
              <div><b>Party:</b> {h.party||'—'}</div>
              <div><b>Origin:</b> {h.origin||'—'}</div>
              <div><b>Shipping Line:</b> {h.shipping_line||'—'}</div>
              <div><b>CHA:</b> {h.cha||'—'}</div>
              <div><b>Weight:</b> {h.wt_mt?`${parseFloat(h.wt_mt).toFixed(3)} MT`:'—'}</div>
              <div><b>Rate:</b> {h.rate_usd?`$${h.rate_usd}/MT`:'—'}</div>
              <div><b>USD Value:</b> {h.usd_value?parseFloat(h.usd_value).toFixed(2):'—'}</div>
              <div><b>Exch Rate:</b> {h.exchange_rate||'—'}</div>
              <div><b>Containers:</b> {h.containers||'—'}</div>
              {h.interest_usd ? <div><b>Interest USD:</b> {h.interest_usd}</div> : null}
              <div style={{gridColumn:'span 4'}}><b>Narration:</b> {voucher.narration||'—'}</div>
            </div>
          </div>

          <div className="section-title" style={{marginTop:8}}>Landed Cost Breakup</div>
          <SectionedJVTable lines={lines} voucher={voucher} />
          <JVSummaryGrid totals={voucher.entries?.totals||{}} voucher={voucher} />
        </div>
        <div style={{marginTop:16,paddingTop:14,borderTop:'1px solid var(--border)',display:'flex',justifyContent:'flex-end'}}>
          <div className="btn-group">
            <button className="btn btn-outline btn-sm" onClick={onOpenOrder}>Open Order</button>
            <button className="btn btn-secondary btn-sm" onClick={onRegenerate} disabled={busy}>{busy?'...':'Regenerate'}</button>
            <button className="btn btn-success btn-sm" onClick={onPrint}>Print</button>
          </div>
        </div>
      </div>
    </div>
  );
}
